/**
 * AdvancedFilter — a schema-driven, multi-condition filter builder (Jira/Linear
 * class) in an anchored popover. Hybrid layout:
 *   • Common   — quick range controls for numeric + date fields (min/max, from/to)
 *   • Advanced — a condition builder: field → operator → value, Match All/Any
 *
 * Apply-gated: edits live in a draft, a live "N of M match" count previews, and
 * the caller's rows only update on Apply (Clear all resets). Driven by a `fields`
 * schema + an `accessor`, so wiring a real backend later = swap the schema + data.
 * This powers audit-log filtering.
 *
 * The predicate helpers are exported so the same model can filter the table AND
 * drive the live count: `emptyFilterModel`, `filterModelActive`, `buildPredicate`.
 */
import { For, Show, createSignal, type JSX } from "solid-js";
import ListFilter from "lucide-solid/icons/list-filter";
import Trash2 from "lucide-solid/icons/trash-2";
import Plus from "lucide-solid/icons/plus";
import { Popover } from "./popover";
import { Button } from "./button";
import { Select, type SelectOption } from "./toolbar";
import { MultiSelect } from "./multi-select";
import { CountChip } from "./lozenge";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";

export type FilterFieldType = "enum" | "number" | "date" | "text";
export interface FilterOption {
  value: string;
  label: string;
}
export interface FilterField {
  key: string;
  label: string;
  type: FilterFieldType;
  options?: FilterOption[]; // for enum
}

export interface Condition {
  id: number;
  field: string;
  operator: string;
  value: unknown;
}
export interface FilterModel {
  combinator: "all" | "any";
  conditions: Condition[];
  ranges: Record<string, [number | null, number | null]>;
  dates: Record<string, [string | null, string | null]>;
}

export const emptyFilterModel = (): FilterModel => ({
  combinator: "all",
  conditions: [],
  ranges: {},
  dates: {},
});

const OPERATORS: Record<FilterFieldType, { value: string; label: string }[]> = {
  enum: [
    { value: "in", label: "is any of" },
    { value: "nin", label: "is none of" },
  ],
  number: [
    { value: "eq", label: "=" },
    { value: "ne", label: "≠" },
    { value: "gt", label: ">" },
    { value: "gte", label: "≥" },
    { value: "lt", label: "<" },
    { value: "lte", label: "≤" },
    { value: "between", label: "is between" },
  ],
  date: [
    { value: "on", label: "on" },
    { value: "before", label: "before" },
    { value: "after", label: "after" },
    { value: "between", label: "is between" },
  ],
  text: [
    { value: "contains", label: "contains" },
    { value: "is", label: "is" },
    { value: "isnot", label: "is not" },
    { value: "starts", label: "starts with" },
  ],
};

const toNum = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isNaN(v) ? null : v;
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? null : n;
};
const toDay = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const s = /^\d{4}-\d{2}-\d{2}$/.test(String(v)) ? `${v}T00:00:00` : String(v);
  const d = new Date(s);
  return Number.isNaN(+d) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

function evalCondition(type: FilterFieldType, op: string, raw: unknown, value: unknown): boolean {
  if (type === "enum") {
    const set = (value as string[]) ?? [];
    if (set.length === 0) return true;
    const has = set.includes(String(raw));
    return op === "nin" ? !has : has;
  }
  if (type === "number") {
    const a = toNum(raw);
    if (a == null) return false;
    if (op === "between") {
      const [lo, hi] = (value as [number | null, number | null]) ?? [null, null];
      if (lo != null && a < lo) return false;
      if (hi != null && a > hi) return false;
      return true;
    }
    const b = toNum(value);
    if (b == null) return true;
    return op === "eq" ? a === b : op === "ne" ? a !== b : op === "gt" ? a > b : op === "gte" ? a >= b : op === "lt" ? a < b : a <= b;
  }
  if (type === "date") {
    const a = toDay(raw);
    if (a == null) return false;
    if (op === "between") {
      const [lo, hi] = (value as [string | null, string | null]) ?? [null, null];
      const l = toDay(lo),
        h = toDay(hi);
      if (l != null && a < l) return false;
      if (h != null && a > h) return false;
      return true;
    }
    const b = toDay(value);
    if (b == null) return true;
    return op === "on" ? a === b : op === "before" ? a < b : a > b;
  }
  // text
  const a = String(raw ?? "").toLowerCase();
  const b = String(value ?? "").toLowerCase();
  if (!b) return true;
  return op === "is" ? a === b : op === "isnot" ? a !== b : op === "starts" ? a.startsWith(b) : a.includes(b);
}

/** Count of active constraints in a model — drives the Filter pill badge. */
export function filterModelActive(m: FilterModel | null): number {
  if (!m) return 0;
  let n = m.conditions.length;
  for (const r of Object.values(m.ranges)) if (r[0] != null || r[1] != null) n++;
  for (const d of Object.values(m.dates)) if (d[0] || d[1]) n++;
  return n;
}

/**
 * Build a row predicate from a model. Common ranges/dates always AND; advanced
 * conditions combine by the model combinator. Shared by the live count + table.
 */
export function buildPredicate<T>(
  model: FilterModel | null,
  fields: FilterField[],
  accessor: (row: T, key: string) => unknown,
): (row: T) => boolean {
  if (!model) return () => true;
  const typeOf = (key: string) => fields.find((f) => f.key === key)?.type ?? "text";
  return (row: T) => {
    for (const [key, [lo, hi]] of Object.entries(model.ranges)) {
      if (lo == null && hi == null) continue;
      if (!evalCondition("number", "between", accessor(row, key), [lo, hi])) return false;
    }
    for (const [key, [from, to]] of Object.entries(model.dates)) {
      if (!from && !to) continue;
      if (!evalCondition("date", "between", accessor(row, key), [from, to])) return false;
    }
    if (model.conditions.length === 0) return true;
    const results = model.conditions.map((c) => evalCondition(typeOf(c.field), c.operator, accessor(row, c.field), c.value));
    return model.combinator === "all" ? results.every(Boolean) : results.some(Boolean);
  };
}

let conditionId = 0;

const COMBINATORS: SelectOption<"all" | "any">[] = [
  { value: "all", label: "all" },
  { value: "any", label: "any" },
];

const fieldInput = cn(
  "h-8 w-full min-w-0 rounded-small border border-[color:var(--c9-border-input)] bg-[var(--c9-bg-input)]",
  "px-100 text-body text-[color:var(--c9-text)] hover:border-[color:var(--c9-border-bold)]",
  "placeholder:text-[color:var(--c9-text-subtlest)]",
  focusRing,
);

/** The value control for a single condition, switched on field type + operator. */
function ConditionValue(props: { field: FilterField; operator: string; value: unknown; onChange: (v: unknown) => void }): JSX.Element {
  return (
    <Show when={props.field} keyed>
      {(f) => (
        <>
          <Show when={f.type === "enum"}>
            <MultiSelect
              options={f.options ?? []}
              value={(props.value as string[]) ?? []}
              onChange={props.onChange}
              placeholder="Select…"
              width={220}
              class="w-full"
            />
          </Show>
          <Show when={f.type === "number"}>
            <Show
              when={props.operator === "between"}
              fallback={
                <input
                  type="number"
                  class={fieldInput}
                  value={(props.value as string) ?? ""}
                  onInput={(e) => props.onChange(e.currentTarget.value)}
                  placeholder="Value"
                />
              }
            >
              <div class="flex items-center gap-100">
                <input
                  type="number"
                  class={fieldInput}
                  value={((props.value as [string, string]) ?? [])[0] ?? ""}
                  onInput={(e) => props.onChange([e.currentTarget.value, ((props.value as [string, string]) ?? [])[1] ?? ""])}
                  placeholder="Min"
                />
                <span class="text-[color:var(--c9-text-subtlest)]">–</span>
                <input
                  type="number"
                  class={fieldInput}
                  value={((props.value as [string, string]) ?? [])[1] ?? ""}
                  onInput={(e) => props.onChange([((props.value as [string, string]) ?? [])[0] ?? "", e.currentTarget.value])}
                  placeholder="Max"
                />
              </div>
            </Show>
          </Show>
          <Show when={f.type === "date"}>
            <Show
              when={props.operator === "between"}
              fallback={
                <input
                  type="date"
                  class={fieldInput}
                  value={(props.value as string) ?? ""}
                  onInput={(e) => props.onChange(e.currentTarget.value)}
                />
              }
            >
              <div class="flex items-center gap-100">
                <input
                  type="date"
                  class={fieldInput}
                  value={((props.value as [string, string]) ?? [])[0] ?? ""}
                  onInput={(e) => props.onChange([e.currentTarget.value, ((props.value as [string, string]) ?? [])[1] ?? ""])}
                />
                <span class="text-[color:var(--c9-text-subtlest)]">–</span>
                <input
                  type="date"
                  class={fieldInput}
                  value={((props.value as [string, string]) ?? [])[1] ?? ""}
                  onInput={(e) => props.onChange([((props.value as [string, string]) ?? [])[0] ?? "", e.currentTarget.value])}
                />
              </div>
            </Show>
          </Show>
          <Show when={f.type === "text"}>
            <input
              type="text"
              class={fieldInput}
              value={(props.value as string) ?? ""}
              onInput={(e) => props.onChange(e.currentTarget.value)}
              placeholder="Value"
            />
          </Show>
        </>
      )}
    </Show>
  );
}

export function AdvancedFilter<T>(props: {
  fields: FilterField[];
  commonFields?: string[];
  rows: T[];
  accessor: (row: T, key: string) => unknown;
  applied: FilterModel | null;
  onApply: (model: FilterModel | null) => void;
}): JSX.Element {
  // eslint-disable-next-line solid/reactivity -- intentional one-time seed; the draft diverges from `applied` while the user edits, then commits via onApply
  const [draft, setDraft] = createSignal<FilterModel>(props.applied ?? emptyFilterModel());

  const fieldByKey = (k: string) => props.fields.find((f) => f.key === k)!;
  const defaultOperator = (k: string) => OPERATORS[fieldByKey(k).type][0]!.value;

  const matchCount = () => props.rows.filter(buildPredicate(draft(), props.fields, props.accessor)).length;

  const setRange = (key: string, idx: 0 | 1, raw: string) => {
    const n = raw === "" ? null : Number(raw);
    setDraft((m) => {
      const cur = m.ranges[key] ?? [null, null];
      const next: [number | null, number | null] = idx === 0 ? [n, cur[1]] : [cur[0], n];
      return { ...m, ranges: { ...m.ranges, [key]: next } };
    });
  };
  const setDate = (key: string, idx: 0 | 1, raw: string) => {
    setDraft((m) => {
      const cur = m.dates[key] ?? [null, null];
      const next: [string | null, string | null] = idx === 0 ? [raw || null, cur[1]] : [cur[0], raw || null];
      return { ...m, dates: { ...m.dates, [key]: next } };
    });
  };

  const addCondition = () => {
    const first = props.fields[0];
    if (!first) return;
    setDraft((m) => ({
      ...m,
      conditions: [...m.conditions, { id: ++conditionId, field: first.key, operator: defaultOperator(first.key), value: undefined }],
    }));
  };
  const updateCondition = (id: number, patch: Partial<Condition>) =>
    setDraft((m) => ({ ...m, conditions: m.conditions.map((c) => (c.id === id ? { ...c, ...patch } : c)) }));
  const removeCondition = (id: number) =>
    setDraft((m) => ({ ...m, conditions: m.conditions.filter((c) => c.id !== id) }));

  const common = () => (props.commonFields ?? []).map(fieldByKey);

  return (
    <Popover
      width={560}
      trigger={({ toggle, open, setRef }) => {
        const active = () => filterModelActive(props.applied);
        return (
          <span ref={setRef} class="inline-flex">
            <button
              type="button"
              onClick={toggle}
              aria-haspopup="dialog"
              aria-expanded={open()}
              class={cn(
                "inline-flex h-8 shrink-0 items-center gap-075 rounded-[3px] px-100 text-body font-[570] transition-colors",
                active() > 0
                  ? "bg-[var(--c9-bg-selected)] text-[color:var(--c9-text-selected)]"
                  : "bg-transparent text-[color:var(--c9-text-subtle)] hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                focusRing,
              )}
            >
              <ListFilter size={16} />
              <span>Filter</span>
              <Show when={active() > 0}>
                <CountChip value={active()} />
              </Show>
            </button>
          </span>
        );
      }}
    >
      {({ close }) => (
        <>
          <div class="min-h-0 flex-1 overflow-y-auto">
            {/* Common ranges */}
            <Show when={common().length > 0}>
              <div class="flex flex-col gap-150 border-b border-[color:var(--c9-border)] p-300">
                <For each={common()}>
                  {(f) => (
                    <div class="grid grid-cols-[6rem_minmax(0,1fr)] items-center gap-200">
                      <span class="text-body-s font-[630] text-[color:var(--c9-text-subtle)]">{f.label}</span>
                      <Show
                        when={f.type === "date"}
                        fallback={
                          <div class="flex items-center gap-100">
                            <input
                              type="number"
                              class={fieldInput}
                              placeholder="Min"
                              value={draft().ranges[f.key]?.[0] ?? ""}
                              onInput={(e) => setRange(f.key, 0, e.currentTarget.value)}
                            />
                            <span class="text-[color:var(--c9-text-subtlest)]">–</span>
                            <input
                              type="number"
                              class={fieldInput}
                              placeholder="Max"
                              value={draft().ranges[f.key]?.[1] ?? ""}
                              onInput={(e) => setRange(f.key, 1, e.currentTarget.value)}
                            />
                          </div>
                        }
                      >
                        <div class="flex items-center gap-100">
                          <input
                            type="date"
                            class={fieldInput}
                            value={draft().dates[f.key]?.[0] ?? ""}
                            onInput={(e) => setDate(f.key, 0, e.currentTarget.value)}
                          />
                          <span class="text-[color:var(--c9-text-subtlest)]">–</span>
                          <input
                            type="date"
                            class={fieldInput}
                            value={draft().dates[f.key]?.[1] ?? ""}
                            onInput={(e) => setDate(f.key, 1, e.currentTarget.value)}
                          />
                        </div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
            </Show>

            {/* Advanced condition builder */}
            <div class="flex flex-col gap-150 p-300">
              <div class="flex items-center gap-100 text-body text-[color:var(--c9-text-subtle)]">
                <span>Match</span>
                <Select
                  value={draft().combinator}
                  options={COMBINATORS}
                  onChange={(v) => setDraft((m) => ({ ...m, combinator: v }))}
                  class="w-[4.5rem]"
                  label="Match all or any"
                />
                <span>of the following</span>
              </div>

              <For each={draft().conditions} fallback={<p class="text-body-s text-[color:var(--c9-text-subtlest)]">No conditions yet.</p>}>
                {(c) => {
                  const field = () => fieldByKey(c.field);
                  return (
                    <div class="flex items-center gap-100">
                      <div class="w-[8.5rem] shrink-0">
                        <Select
                          value={c.field}
                          options={props.fields.map((f) => ({ value: f.key, label: f.label }))}
                          onChange={(v) => updateCondition(c.id, { field: v, operator: defaultOperator(v), value: undefined })}
                          label="Field"
                          class="w-full"
                        />
                      </div>
                      <div class="w-[6.5rem] shrink-0">
                        <Select
                          value={c.operator}
                          options={OPERATORS[field().type]}
                          onChange={(v) => updateCondition(c.id, { operator: v })}
                          label="Operator"
                          class="w-full"
                        />
                      </div>
                      <div class="min-w-0 flex-1">
                        <ConditionValue field={field()} operator={c.operator} value={c.value} onChange={(v) => updateCondition(c.id, { value: v })} />
                      </div>
                      <button
                        type="button"
                        aria-label="Remove condition"
                        onClick={() => removeCondition(c.id)}
                        class={cn(
                          "inline-flex size-8 shrink-0 items-center justify-center rounded-[3px] text-[color:var(--c9-icon-subtle)]",
                          "hover:bg-[var(--c9-bg-neutral-subtle-hovered)]",
                          focusRing,
                        )}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  );
                }}
              </For>

              <div>
                <Button appearance="subtle" iconBefore={<Plus size={16} />} onClick={addCondition}>
                  Add condition
                </Button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div class="flex shrink-0 items-center justify-between gap-200 border-t border-[color:var(--c9-border)] px-300 py-200">
            <span class="text-body-s text-[color:var(--c9-text-subtle)]">
              {matchCount()} of {props.rows.length} match
            </span>
            <div class="flex items-center gap-100">
              <Button
                appearance="subtle"
                onClick={() => {
                  setDraft(emptyFilterModel());
                  props.onApply(null);
                }}
              >
                Clear all
              </Button>
              <Button
                appearance="primary"
                onClick={() => {
                  const m = draft();
                  props.onApply(filterModelActive(m) > 0 ? m : null);
                  close();
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </>
      )}
    </Popover>
  );
}
